/**
 * The answer key of a picross is the picture itself. Kept apart from the canvas component so
 * the eagerly loaded module file never pulls the author's form into the player bundle.
 */
import { blankRows, resizeRows } from "./progress";
import { EMPTY, FILLED, type PicrossDefinition, type PicrossSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no rows at all, so the form can tell
 * "nothing typed" from a deliberately blank picture. `pictureOf` pads it to a blank grid.
 */
export function emptySolution(_definition: PicrossDefinition): PicrossSolution {
  return { rows: [] };
}

/** Nothing queued: not a picture, or the empty one the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isPicture(value) || value.rows.length === 0;
}

/** Whether a stored value is a picture at all, whatever its size. */
export function isPicture(value: unknown): value is PicrossSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const rows = (value as { rows?: unknown }).rows;
  return Array.isArray(rows) && rows.every((row) => typeof row === "string" && /^[#.]*$/.test(row));
}

/** Reads whatever is stored, however wrong, as a picture of the definition's size. */
export function pictureOf(value: unknown, definition: PicrossDefinition): string[] {
  if (!isPicture(value)) return blankRows(definition.rows, definition.cols);
  return resizeRows(
    value.rows.map((row) => Array.from(row, (c) => (c === FILLED ? FILLED : EMPTY)).join("")),
    definition.rows,
    definition.cols,
  );
}
